export const CARD_BRAND_LABELS = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
};

/**
 * Client-side card brand detection (mirrors server cardValidation.js).
 */
export function detectCardType(cardNumber) {
  if (!cardNumber) return null;
  const digits = String(cardNumber).replace(/\D/g, '');

  if (/^4/.test(digits)) return 'visa';
  if (/^5[1-5]/.test(digits) || /^2[2-7][2-9][0-9]/.test(digits)) return 'mastercard';
  if (/^3[47]/.test(digits)) return 'amex';
  if (/^6011/.test(digits) || /^65/.test(digits) || /^64[4-9]/.test(digits)) return 'discover';
  if (/^3[068]/.test(digits)) return 'diners';
  if (/^35/.test(digits)) return 'jcb';
  return 'unknown';
}

export function formatCardNumberInput(value) {
  const raw = String(value).replace(/\D/g, '');
  const type = detectCardType(raw) || 'unknown';
  let digits = raw;
  if (type === 'amex') digits = raw.slice(0, 15);
  else if (type === 'diners') digits = raw.slice(0, 14);
  else digits = raw.slice(0, 16);

  if (type === 'amex') {
    const p1 = digits.slice(0, 4);
    const p2 = digits.slice(4, 10);
    const p3 = digits.slice(10, 15);
    return [p1, p2, p3].filter(Boolean).join(' ');
  }
  if (type === 'diners') {
    const p1 = digits.slice(0, 4);
    const p2 = digits.slice(4, 10);
    const p3 = digits.slice(10, 14);
    return [p1, p2, p3].filter(Boolean).join(' ');
  }
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

/** Digit group sizes per brand for zero-fill mask display. */
export function getCardNumberMaskGroups(brand) {
  switch (brand) {
    case 'amex':
      return [4, 6, 5];
    case 'diners':
      return [4, 6, 4];
    default:
      return [4, 4, 4, 4];
  }
}

/**
 * Once brand is recognized, build a visual mask like 4111 0000 0000 0000 or 3744 000000 00000.
 * Typed digits stay real; remaining slots show 0 (rendered as a hint behind the input).
 */
export function buildCardNumberMaskDisplay(digits, brand) {
  const type = brand && brand !== 'unknown' ? brand : detectCardType(digits);
  if (!type || type === 'unknown') return null;

  const groups = getCardNumberMaskGroups(type);
  const maxLen = groups.reduce((sum, n) => sum + n, 0);
  const typed = String(digits).replace(/\D/g, '').slice(0, maxLen);
  const chars = typed.split('');
  while (chars.length < maxLen) chars.push('0');

  let offset = 0;
  return groups
    .map((size) => {
      const chunk = chars.slice(offset, offset + size).join('');
      offset += size;
      return chunk;
    })
    .join(' ');
}

/** Build a zero-fill hint from a template like 00/00, 000, or 00000. */
export function buildZeroMaskFromTemplate(value, template) {
  if (!template) return null;
  const typed = String(value).replace(/\D/g, '');
  let digitIndex = 0;
  return template
    .split('')
    .map((ch) => {
      if (/\d/.test(ch)) {
        if (digitIndex < typed.length) return typed[digitIndex++];
        return '0';
      }
      return ch;
    })
    .join('');
}

export function formatExpiryInput(value) {
  let v = String(value).replace(/\D/g, '').slice(0, 4);
  if (v.length >= 3) v = `${v.slice(0, 2)}/${v.slice(2)}`;
  return v;
}

export function formatCvvInput(value, brand) {
  const max = brand === 'amex' ? 4 : 3;
  return String(value).replace(/\D/g, '').slice(0, max);
}

export function formatZipInput(value) {
  return String(value).replace(/\D/g, '').slice(0, 5);
}

export function getCvvMaskTemplate(brand) {
  return brand === 'amex' ? '0000' : '000';
}

/** Map Square Web Payments SDK CardBrand enum → our icon keys */
export function mapSquareCardBrand(squareBrand) {
  if (!squareBrand) return null;
  const b = String(squareBrand).toUpperCase().replace(/[\s-]+/g, '_');
  if (b === 'VISA') return 'visa';
  if (b === 'MASTERCARD' || b === 'MASTER_CARD') return 'mastercard';
  if (b === 'AMERICAN_EXPRESS' || b === 'AMEX') return 'amex';
  if (b === 'DISCOVER' || b === 'DISCOVER_DINERS') return 'discover';
  if (b.includes('DINERS')) return 'diners';
  if (b === 'JCB') return 'jcb';
  return null;
}

export const ACCEPTED_CARD_BRANDS = ['visa', 'mastercard', 'amex', 'discover'];
